import moment from 'moment';
import { isEmpty } from '@jjj/components';
import actions from './actions';
import { createReducer, getError } from '../../../../../../helpers/reducer';
import { keyBy } from '../../../../../../helpers/core';
import { convertBreakDurationToMinutes, withTZ } from '../../../../../../helpers/dateTime';
import { t } from '../../../../../../i18n';

const initState = {
  editForm: null,
  baseError: null,
  loading: false,
  isEditShiftLoading: false,
  formInfo: null,
  finished_at: null,
  total: 0,
};

function formatShift(shift, formInfo) {
  const breakDuration = shift?.approved_break_duration;

  if (!breakDuration?.value) {
    return shift;
  }

  // The label comes translated; the 15-minute list in the same locale still names every pre-existing value.
  const breakItem = formInfo?.break_durations?.find(item => item.name === breakDuration.value);
  const value = convertBreakDurationToMinutes(breakItem ? Number(breakItem.id) : breakDuration.value);

  return { ...shift, approved_break_duration: { ...breakDuration, value } };
}

function validate(state, attr, value, value2, value3) {
  let error = '';
  const finishedAtValue = attr === 'finished_at' && value;
  const startAtValue = attr === 'started_at' && value;

  if (attr === 'started_at') {
    if (isEmpty(startAtValue)) {
      error = t('dash.validation.choose_a_field', { fieldName: t('dash.jobs.bookings.start_date') });
    } else if (startAtValue && withTZ(startAtValue).isSameOrAfter(withTZ(value3))) {
      return t('dash.validation.start_time_error');
    }
  }

  if (attr === 'finished_at') {
    if (finishedAtValue && withTZ(finishedAtValue).isSameOrBefore(withTZ(value2))) {
      return t('dash.validation.finish_time_error');
    }

    const shiftStatuses = [
      'closed_by_worker',
      'closed_by_manager',
      'completed_final_approval',
      'business_paid',
      'worker_paid',
    ];

    // if finished date was presented initially or shift is closed set the field as required
    if (isEmpty(value) && (state.finished_at || shiftStatuses.includes(state.editForm.status?.value))) {
      error = t('dash.validation.choose_a_field', { fieldName: t('dash.jobs.bookings.end_date') });
    }
  }

  if (attr === 'client_rating') {
    if (isEmpty(value)) {
      error = t('dash.validation.select_a_field', { fieldName: t('dash.jobs.bookings.unit_rating_label') });
    }
  }

  if (attr === 'talent_rating') {
    if (isEmpty(value)) {
      error = t('dash.validation.select_a_field', { fieldName: t('dash.jobs.bookings.talent_rating_label') });
    }
  }

  if (attr === 'payment_method') {
    if (isEmpty(value)) {
      error = t('dash.validation.select_a_field', { fieldName: t('dash.jobs.bookings.payment_method_label') });
    }
  }

  return error;
}

function onLoad(state) {
  return {
    ...state,
    isEditShiftLoading: true,
  };
}

function onLoadSuccess(state, { payload }) {
  const { data, shiftToEdit } = payload;
  delete shiftToEdit.payload.id;

  return {
    ...state,
    loading: false,
    isEditShiftLoading: false,
    formInfo: data,
    editForm: formatShift(shiftToEdit.payload, data),
    finished_at: shiftToEdit.payload.finished_at,
  };
}

function onClearStore() {
  return { ...initState };
}

function onSaveSuccess(state) {
  return {
    ...state,
    isEdit: false,
    editForm: null,
  };
}

function onSaveError(state, { payload }) {
  const { data } = payload;

  const propsArray = Object.entries(state.editForm);
  const editForm = keyBy(
    propsArray,
    entry => entry[0],
    entry => {
      const [, value] = entry;

      return {
        ...value,
        error: getError(entry[0], data),
        touched: true,
      };
    },
  );

  const baseError = data.base ? data.base[0] : null;

  return {
    ...state,
    editForm,
    baseError,
  };
}

function onChangeProperty(state, { payload }) {
  const { attr, value } = payload;
  const { editForm } = state;

  const value2 = attr === 'finished_at' ? moment.utc(editForm.started_at.value) : null;
  const value3 = attr === 'started_at' ? moment.utc(editForm.finished_at.value) : null;

  editForm[attr] = {
    value,
    error: validate(state, attr, value, value2, value3),
    name: attr,
    touched: true,
  };

  return {
    ...state,
    editForm: {
      ...editForm,
    },
  };
}

function onCancel(state) {
  return {
    ...state,
    isEdit: false,
    editForm: null,
  };
}

function onTouch(state, { payload }) {
  const propsArray = Object.entries(payload.editForm);
  const editForm = keyBy(
    propsArray,
    entry => entry[0],
    entry => {
      const [, field] = entry;

      return {
        ...field,
        touched: true,
      };
    },
  );

  return {
    ...state,
    editForm,
  };
}

export default createReducer(initState, {
  [actions.LOAD]: onLoad,
  [actions.LOAD_SUCCESS]: onLoadSuccess,
  [actions.CLEAR_STORE]: onClearStore,
  [actions.CHANGE_PROPERTY]: onChangeProperty,
  [actions.CANCEL]: onCancel,
  [actions.SAVE_SUCCESS]: onSaveSuccess,
  [actions.SAVE_ERROR]: onSaveError,
  [actions.TOUCH]: onTouch,
});
